// internal deps
import { None, Option } from "@swiggy-private/js-fp";
import { format } from "@swiggy-private/core-ui";
import { AnalyticsEventPayload } from "@swiggy-private/analytics";
import {
    PaymentFallbackMethod,
    PaymentAlertActionProps,
    OnPayExtraParams,
    OrderData,
} from "@swiggy-private/payment-types";
import { PaymentMethodDef } from "@swiggy-private/payment-ui";
import { CheckoutAction, PlaceOrderData } from "@swiggy-private/checkout-core";

// constants
import { PAYMENT_FALLBACK_TYPES } from "@payment-client/constants/PaymentFallback";

// interfaces
import { GetPaymentMethodsResponse } from "@payment-client/interfaces/PaymentApi";

//helpers
import { getPaymentsCheckoutState, isPaymentMethodEnabled } from "./PaymentHelper";
import { findWalletPaymentMethod } from "./WalletHelper";
import { findPayLaterPaymentMethod } from "./PaylaterHelper";
import { findSwiggyInstrumentsPaymentMethod } from "./SwiggyInstrumentsHelper";
import { getCODPaymentMethod } from "./CodHelper";
import { isInAppUpiMethodInOrderData } from "./InAppUpiHelper";

//constants
import { BALANCE_X, PAY_USING_X } from "@payment-client/constants/TextDict";

// analytics
import {
    paymentFailedDialogDismissClickEvent,
    paymentFailedDialogRetryClickEvent,
    paymentFailedDialogTryOtherClickEvent,
} from "@payment-client/analytics/events/PaymentFailed";

interface GetFallbackActionsParams {
    fallbackMethods: PaymentFallbackMethod[];
    paymentGroups: GetPaymentMethodsResponse["paymentGroup"];
    amountToPay: string;
    payAction: (paymentMethodData: PaymentMethodDef, _extraParams?: OnPayExtraParams) => void;
    setCheckoutState: React.Dispatch<CheckoutAction>;
    placeOrderData: PlaceOrderData;
    failedOrder?: OrderData;
    clickEvent: (payload: AnalyticsEventPayload) => void;
}

export const getPaymentFallbackType = (code?: string): string => {
    if (code == null) {
        return PAYMENT_FALLBACK_TYPES.TRY_OTHERS;
    } else if (code.indexOf("Cash") > -1) {
        return PAYMENT_FALLBACK_TYPES.COD;
    }
    // This is to remove wallet methods suffix that comes like PayTM-SSO
    return code.replace("-SSO", "");
};

export const isValidFallbackMethod = (paymentMethod: Option<PaymentMethodDef>): boolean =>
    paymentMethod.isSome() && isPaymentMethodEnabled(paymentMethod.unwrap());

export const isBalanceSufficient = (amountToPay: number, balance?: number | string): boolean =>
    balance != null && Number(balance) >= amountToPay;

export const getFallbackPaymentMethodData = (
    fallbackMethod: PaymentFallbackMethod,
    paymentGroups: GetPaymentMethodsResponse["paymentGroup"],
    amountToPay: number,
): Option<PaymentMethodDef> => {
    const paymentCode = getPaymentFallbackType(fallbackMethod.paymentCode);

    switch (paymentCode) {
        case PAYMENT_FALLBACK_TYPES.PAYTM:
        case PAYMENT_FALLBACK_TYPES.FREE_CHARGE:
        case PAYMENT_FALLBACK_TYPES.MOBIKWIK:
        case PAYMENT_FALLBACK_TYPES.PHONEPE: {
            const walletMethod = findWalletPaymentMethod(paymentGroups, paymentCode);

            if (!isValidFallbackMethod(walletMethod) || !isBalanceSufficient(amountToPay, fallbackMethod.balance)) {
                return None;
            }
            return walletMethod;
        }

        case PAYMENT_FALLBACK_TYPES.LAZYPAY: {
            const paylaterMethod = findPayLaterPaymentMethod(paymentGroups, paymentCode);
            return isValidFallbackMethod(paylaterMethod) ? paylaterMethod : None;
        }

        case PAYMENT_FALLBACK_TYPES.SWIGGY_MONEY: {
            const swiggyMoneyMethod = findSwiggyInstrumentsPaymentMethod(paymentGroups, paymentCode);
            return isValidFallbackMethod(swiggyMoneyMethod) ? swiggyMoneyMethod : None;
        }

        case PAYMENT_FALLBACK_TYPES.COD: {
            const codMethod = getCODPaymentMethod(paymentGroups);
            return isValidFallbackMethod(codMethod) ? codMethod : None;
        }
    }
    return None;
};

export const getPaymentFallbackActions = ({
    fallbackMethods,
    paymentGroups,
    amountToPay,
    payAction,
    setCheckoutState,
    placeOrderData,
    failedOrder,
    clickEvent,
}: GetFallbackActionsParams): PaymentAlertActionProps[] => {
    return fallbackMethods.reduce<PaymentAlertActionProps[]>((fallbackActions, fallbackMethod) => {
        const paymentFallbackType = getPaymentFallbackType(fallbackMethod.paymentCode);

        switch (paymentFallbackType) {
            case PAYMENT_FALLBACK_TYPES.PAYTM:
            case PAYMENT_FALLBACK_TYPES.FREE_CHARGE:
            case PAYMENT_FALLBACK_TYPES.MOBIKWIK:
            case PAYMENT_FALLBACK_TYPES.PHONEPE:
            case PAYMENT_FALLBACK_TYPES.SWIGGY_MONEY:
            case PAYMENT_FALLBACK_TYPES.LAZYPAY:
            case PAYMENT_FALLBACK_TYPES.COD: {
                const paymentMethodData = getFallbackPaymentMethodData(
                    fallbackMethod,
                    paymentGroups,
                    Number(amountToPay),
                );

                if (paymentMethodData.isSome()) {
                    fallbackActions.push({
                        text: format(PAY_USING_X, fallbackMethod.displayName),
                        subText: fallbackMethod.balance != null ? format(BALANCE_X, fallbackMethod.balance) : undefined,
                        onClick: (): void => {
                            payAction(paymentMethodData.unwrap());
                            // Handled Swiggy Money & Regular Payments Checkout State
                            const checkoutState = getPaymentsCheckoutState();
                            setCheckoutState({ type: checkoutState });
                        },
                    });
                }
                break;
            }

            case PAYMENT_FALLBACK_TYPES.RETRY: {
                fallbackActions.push({
                    text: fallbackMethod.displayName,
                    onClick: (): void => {
                        paymentFailedDialogRetryClickEvent(clickEvent);
                        payAction(placeOrderData.paymentMethodData, placeOrderData.extraParams);
                    },
                });
                break;
            }

            case PAYMENT_FALLBACK_TYPES.TRY_OTHERS:
            case PAYMENT_FALLBACK_TYPES.DISMISS: {
                fallbackActions.push({
                    text: fallbackMethod.displayName,
                    onClick: (): void => {
                        const fn =
                            paymentFallbackType === PAYMENT_FALLBACK_TYPES.TRY_OTHERS
                                ? paymentFailedDialogTryOtherClickEvent
                                : paymentFailedDialogDismissClickEvent;
                        fn(clickEvent);
                        // Handled Swiggy Money & Regular Payments Checkout State
                        const checkoutState = getPaymentsCheckoutState();

                        setCheckoutState({
                            type: checkoutState,
                            payload: {
                                reload: isInAppUpiMethodInOrderData(failedOrder),
                            },
                        });
                    },
                });
                break;
            }
            default:
                break;
        }
        return fallbackActions;
    }, []);
};
